import React, { useEffect, useState } from "react";
import Nav from "../../common/components/Nav";
import Footer from "../../common/components/Footer";
import { fetchLatestVideo } from "../../utils/api/socialFeed";
import { SOCIAL_CHANNELS } from "../../utils/config/socialChannels";

// rss2json returns pubDate as "2026-09-30 09:29:31" in UTC with no timezone
// marker, which browsers would otherwise read as local time.
const parsePublishedAt = (value) => {
  if (!value) return null;
  const parsed = new Date(value.replace(" ", "T") + "Z");
  return isNaN(parsed.getTime()) ? null : parsed;
};

const timeAgo = (date) => {
  if (!date) return "";
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;

  const days = Math.floor(seconds / 86400);
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;

  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
};

// Sample initial feeds / posts data (Replace or map with your live API / Social Wall SDK response)
const INITIAL_FEEDS = [
  {
    id: 1,
    platform: "youtube",
    title: "Lagos State Interactive Analytics & Data Presentation Live Stream",
    author: "Lagos State Official",
    handle: "@LagosStateGov",
    date: "2 hours ago",
    content: "Watch our live townhall presentation covering quarterly statistical analysis and economic metrics.",
    mediaUrl: "https://www.youtube.com/embed/live_stream?channel=YOUR_CHANNEL_ID", // Replace with actual live/embed URL
    isLive: true,
  },
  {
    id: 2,
    platform: "twitter",
    author: "Lagos Analytics Bureau",
    handle: "@LagosAnalytics",
    date: "Today, 10:15 AM",
    content: "We have updated the #SliceAndDice statistical module for 2026! Check out health and education trends across Lagos divisions on our public portal.",
    likes: 124,
    retweets: 42,
    link: "https://twitter.com",
  },
  {
    id: 3,
    platform: "instagram",
    author: "Lagos Bureau of Statistics",
    handle: "@lagos_statistics",
    date: "Yesterday",
    content: "Highlights from the recent Data Summit. Swipe through to see key sector visualisations!",
    imageUrl: "/assets/img/analytics.png",
    likes: 310,
    comments: 18,
    link: "https://instagram.com",
  },
  {
    id: 4,
    platform: "facebook",
    author: "Lagos State Government",
    handle: "@LagosStateGov",
    date: "2 days ago",
    content: "Public notice: The 2026 Q3 Interactive Analysis report is now live on our official open data platform.",
    likes: 88,
    shares: 15,
    link: "https://facebook.com",
  },
];

const Events360Page = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [channelVideos, setChannelVideos] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const [playerStopped, setPlayerStopped] = useState(false);

  // Before anything is picked, show the first channel that returned a video so
  // the large player has real content rather than a placeholder embed. Once the
  // viewer stops playback we hold it empty rather than snapping back to the
  // default channel.
  //
  // channelVideos entries are {id, name, handle, video}, while activeVideo is
  // {channel, video}. The default has to be reshaped or every player read of
  // .channel.* below throws on first render.
  const defaultChannel = channelVideos.length
    ? {
        channel: {
          id: channelVideos[0].id,
          name: channelVideos[0].name,
          handle: channelVideos[0].handle,
        },
        video: channelVideos[0].video,
      }
    : null;

  const playerVideo = playerStopped ? null : activeVideo || defaultChannel;

  const playChannelVideo = (channel, video) => {
    setPlayerStopped(false);
    setActiveVideo({ channel, video });
  };

  const stopPlayback = () => {
    setActiveVideo(null);
    setPlayerStopped(true);
  };

  useEffect(() => {
    let cancelled = false;

    const loadChannels = async () => {
      try {
        const results = await Promise.all(
          SOCIAL_CHANNELS.map(async (channel) => {
            const video = await fetchLatestVideo(channel.id);
            return video ? { ...channel, video } : null;
          })
        );

        if (!cancelled) {
          setChannelVideos(results.filter(Boolean));
        }
      } catch (error) {
        console.error("Error loading official channels:", error);
      } finally {
        if (!cancelled) setChannelsLoading(false);
      }
    };

    loadChannels();

    return () => {
      cancelled = true;
    };
  }, []);

  // Platform badges styling helper
  const renderPlatformBadge = (platform) => {
    switch (platform) {
      case "youtube":
        return (
          <span className="badge rounded-pill px-2.5 py-1 text-bg-danger fw-semibold" style={{ fontSize: "11px" }}>
            YouTube
          </span>
        );
      case "twitter":
        return (
          <span className="badge rounded-pill px-2.5 py-1 text-bg-dark fw-semibold" style={{ fontSize: "11px" }}>
            X / Twitter
          </span>
        );
      case "instagram":
        return (
          <span
            className="badge rounded-pill px-2.5 py-1 text-white fw-semibold"
            style={{
              background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
              fontSize: "11px",
            }}
          >
            Instagram
          </span>
        );
      case "facebook":
        return (
          <span className="badge rounded-pill px-2.5 py-1 text-white fw-semibold" style={{ backgroundColor: "#1877F2", fontSize: "11px" }}>
            Facebook
          </span>
        );
      default:
        return null;
    }
  };

  // Filter feeds based on tab and search
  const filteredFeeds = INITIAL_FEEDS.filter((item) => {
    const matchesTab =
      activeTab === "all"
        ? true
        : activeTab === "live"
        ? item.isLive
        : item.platform === activeTab;

    const matchesSearch =
      item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.author.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesTab && matchesSearch;
  });

  const showComingSoon = ["live", "twitter", "instagram", "facebook"].includes(activeTab);

  return (
    <div
      className="d-flex flex-column min-vh-100"
      style={{
        backgroundColor: "#F8FAFC",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Page Header */}
      <header className="bg-white border-bottom border-light">
        <Nav />
        <div className="py-5" style={{ backgroundColor: "#F1F5F9" }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center">
                <span
                  className="badge rounded-pill px-3 py-2 text-uppercase mb-3 fw-semibold"
                  style={{
                    backgroundColor: "rgba(10, 61, 46, 0.08)",
                    color: "#0A3D2E",
                    fontSize: "12px",
                    letterSpacing: "0.8px",
                  }}
                >
                  Social Media & Broadcasting
                </span>
                <h1 className="fw-bold text-dark display-6 mb-3" style={{ letterSpacing: "-0.5px" }}>
                  Events 360
                </h1>
                <p className="text-secondary fs-6 lh-relaxed mb-0">
                  Stay updated with real-time official broadcasts, social updates, live streams, and public events directly from our social feeds.
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Events & Social Stream Section */}
      <section className="flex-grow-1 py-5">
        <div className="container">

          {/* Controls: Search & Category Filter Tabs */}
          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 mb-4">

            {/* Filter Tabs */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {[
                { id: "all", label: "All Updates" },
                { id: "live", label: "🔴 Live Now" },
                { id: "twitter", label: "X / Twitter" },
                { id: "instagram", label: "Instagram" },
                { id: "youtube", label: "YouTube" },
                { id: "facebook", label: "Facebook" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="btn rounded-pill px-3 py-2 small fw-medium transition-all"
                  style={{
                    backgroundColor: activeTab === tab.id ? "#0A3D2E" : "#FFFFFF",
                    color: activeTab === tab.id ? "#FFFFFF" : "#475569",
                    border: activeTab === tab.id ? "1px solid #0A3D2E" : "1px solid #E2E8F0",
                    fontSize: "13px",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search Box */}
            <div className="w-100 max-w-xs" style={{ maxWidth: "280px" }}>
              <input
                type="text"
                className="form-control rounded-pill px-3 py-2 border-light-subtle shadow-none small"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ fontSize: "13px" }}
              />
            </div>
          </div>

          {showComingSoon ? (
            <div className="bg-white rounded-4 shadow-sm border p-5 text-center">
              <div
                className="mx-auto d-flex align-items-center justify-content-center rounded-circle mb-4"
                style={{ width: "76px", height: "76px", backgroundColor: "rgba(10, 61, 46, 0.08)" }}
              >
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0A3D2E"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9"></circle>
                  <polyline points="12 7 12 12 15.5 14"></polyline>
                </svg>
              </div>
              <h4 className="fw-bold text-dark mb-2">Coming Soon</h4>
              <p className="text-muted mb-0">This platform feed will be available soon.</p>
            </div>
          ) : (
            <>
          {/* Official YouTube Channels: live feed via public channel RSS. Each
              card plays that channel's latest video in a modal on click. */}
          {channelsLoading ? (
            <div className="row g-4 mb-5">
              {SOCIAL_CHANNELS.map((channel) => (
                <div className="col-lg-3 col-md-6" key={channel.id}>
                  <div className="card border-0 rounded-4 bg-white shadow-sm h-100 overflow-hidden">
                    {/* Skeleton uses inline styles rather than Bootstrap's
                        .placeholder helpers, which do not exist in 5.0.2. */}
                    <div className="ratio ratio-16x9">
                      <div style={{ width: "100%", height: "100%", backgroundColor: "#F1F5F9" }}></div>
                    </div>
                    <div className="card-body p-3">
                      <div
                        style={{
                          width: "85%",
                          height: "12px",
                          borderRadius: "4px",
                          backgroundColor: "#F1F5F9",
                          marginBottom: "10px",
                        }}
                      ></div>
                      <div
                        style={{
                          width: "45%",
                          height: "10px",
                          borderRadius: "4px",
                          backgroundColor: "#F1F5F9",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : channelVideos.length > 0 ? (
            <div className="mb-5">
              <div className="d-flex align-items-center gap-2 mb-3">
                <h5 className="fw-bold text-dark mb-0">Official Channels</h5>
                <span className="badge rounded-pill text-white fw-semibold" style={{ backgroundColor: "#0A3D2E", fontSize: "11px" }}>
                  YouTube
                </span>
              </div>

              <div className="row g-4">
                {channelVideos.map(({ id, name, handle, video }) => {
                  const isPlaying = Boolean(
                    playerVideo && playerVideo.video && playerVideo.video.videoId === video.videoId
                  );

                  return (
                    <div className="col-lg-3 col-md-6" key={id}>
                      <button
                        type="button"
                        onClick={() => playChannelVideo({ id, name, handle }, video)}
                        aria-pressed={isPlaying}
                        className="card rounded-4 bg-white shadow-sm h-100 w-100 p-0 text-start overflow-hidden"
                        style={{
                          cursor: "pointer",
                          border: isPlaying ? "2px solid #0A3D2E" : "1px solid transparent",
                        }}
                      >
                      <div className="position-relative">
                        <div className="ratio ratio-16x9 bg-light">
                          <img
                            src={video.thumbnail}
                            alt={video.title}
                            className="w-100 h-100"
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                        <span
                          className="position-absolute d-flex align-items-center justify-content-center"
                          style={{
                            right: "10px",
                            bottom: "10px",
                            width: "38px",
                            height: "38px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(220, 39, 38, 0.92)",
                          }}
                        >
                          {isPlaying ? (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="#ffffff">
                              <rect x="6" y="5" width="4" height="14" rx="1"></rect>
                              <rect x="14" y="5" width="4" height="14" rx="1"></rect>
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="#ffffff">
                              <polygon points="6,4 20,12 6,20"></polygon>
                            </svg>
                          )}
                        </span>
                      </div>

                      <div className="card-body p-3">
                          <h6 className="fw-bold text-dark mb-1 lh-sm" style={{ fontSize: "13.5px" }}>
                            {video.title}
                          </h6>
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="text-muted" style={{ fontSize: "12px" }}>
                              {handle}
                            </span>
                            <span className="text-muted" style={{ fontSize: "12px" }}>
                              {timeAgo(parsePublishedAt(video.publishedAt))}
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Large player. Plays the channel picked from the cards above, and
              falls back to the first available channel so the screen is never
              left showing a placeholder embed. */}
          {(playerVideo || (!playerStopped && channelVideos.length > 0)) && (
            <div className="bg-white p-4 rounded-4 shadow-sm border mb-5">
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="spinner-grow spinner-grow-sm" style={{ color: "#DC2626" }} role="status"></span>
                  <span className="fw-bold text-uppercase small tracking-wider" style={{ letterSpacing: "1px", color: "#DC2626" }}>
                    Now Playing
                  </span>
                </div>
                <span className="text-muted small">
                  {playerVideo
                    ? `Channel: ${playerVideo.channel.name}`
                    : "Channel: Official Lagos State Streams"}
                </span>
              </div>

              <div className="ratio ratio-16x9 rounded-3 overflow-hidden bg-black mb-3">
                {playerVideo ? (
                  <iframe
                    key={playerVideo.video.videoId}
                    src={`https://www.youtube.com/embed/${playerVideo.video.videoId}?autoplay=1&rel=0`}
                    title={playerVideo.video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/live_stream?channel=YOUR_CHANNEL_ID"
                    title="Events 360 Live Stream"
                    allowFullScreen
                  ></iframe>
                )}
              </div>

              {playerVideo ? (
                <>
                  <h5 className="fw-bold text-dark mb-1">{playerVideo.video.title}</h5>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <span className="text-secondary small mb-0">
                      {playerVideo.channel.handle} &middot;{" "}
                      {timeAgo(parsePublishedAt(playerVideo.video.publishedAt))}
                    </span>
                    <button
                      type="button"
                      onClick={stopPlayback}
                      className="btn btn-sm rounded-pill px-3 fw-medium"
                      style={{
                        backgroundColor: "#FFFFFF",
                        color: "#475569",
                        border: "1px solid #E2E8F0",
                        fontSize: "12px",
                      }}
                    >
                      Stop playback
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h5 className="fw-bold text-dark mb-1">
                    Lagos State Interactive Analytics & Data Presentation Live Stream
                  </h5>
                  <p className="text-secondary small mb-0">
                    Join our townhall stream discussing sector data, policy metrics, and public sector dashboards.
                  </p>
                </>
              )}
            </div>
          )}
            </>
          )}


        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Events360Page;